export function calculateTournamentRankings(
    games: any, // TODO: pick better types
    tournamentAppConfiguration: any,
    username: String,
    overallRank: string
) {
    const tournamentSummaryData = new TournamentSummaryData()

    // Process each game to calculate summary statistics
    if (games && games.length > 0 && username) {
        const userScores: number[] = [];
        
        games.forEach((game: { leaderboard: any[]; title: string; }) => {
            // Find user in the leaderboard
            const userEntry = game.leaderboard?.find((entry: { userName: string; }) =>
                entry.userName.toLowerCase() === username.toLowerCase()
            );
// TODO: break into individual parts
            if (userEntry) {
                // Collect user's score
                userScores.push(userEntry.points || 0);

                if (userEntry.initials) {
                    tournamentSummaryData.initialsUsed.add(userEntry.initials);
                }
// TODO: eventually handle ties!
                if (userEntry.rank < tournamentSummaryData.bestRank) {
                    tournamentSummaryData.bestRank = userEntry.rank;
                    tournamentSummaryData.bestGame = game.title;
                }

                if (userEntry.rank > tournamentSummaryData.worstRank) {
                    tournamentSummaryData.worstRank = userEntry.rank;
                    // Clear previous worst games and add the new one
                    tournamentSummaryData.worstGames.length = 0;
                    tournamentSummaryData.worstGames.push(game.title);
                } else if (userEntry.rank === tournamentSummaryData.worstRank) {
                    // Add to worst games if rank is tied
                    tournamentSummaryData.worstGames.push(game.title);
                }
            } else {
                // User not found in this game's leaderboard, add to unranked games
                tournamentSummaryData.unrankedGames.push(game.title);
                // Add penalty score for unranked game
                userScores.push(tournamentAppConfiguration.missingGamePenalty);
            }
        });
        
        // Apply dropNLowest logic to user's total points
        if (tournamentAppConfiguration.dropNLowest > 0 && userScores.length > tournamentAppConfiguration.dropNLowest) {
            const sortedScores = [...userScores].sort((a, b) => 
                tournamentAppConfiguration.morePointsIsBetter ? a - b : b - a
            );
            const scoresToKeep = sortedScores.slice(tournamentAppConfiguration.dropNLowest);
            tournamentSummaryData.totalPoints = scoresToKeep.reduce((sum: number, score: number) => sum + score, 0);
        } else {
            tournamentSummaryData.totalPoints = userScores.reduce((sum: number, score: number) => sum + score, 0);
        }
    }

    // Calculate percentage of total possible points
    // tournamentSummaryData.percentage = totalPossiblePoints > 0
    //     ? Math.round((tournamentSummaryData.totalPoints / totalPossiblePoints) * 100)
    //     : 0;

    // Calculate points needed to increase rank and reach rank 50
    // Default values
    let pointsToNextRank = null;
    let pointsToCutoff = null;

    // Only calculate if we have games and a username
    if (games && games.length > 0 && username && overallRank !== '-' && overallRank !== 'Not ranked') {
        // Create a map to store player data
        const playerMap = new Map();

        // Process each game to collect individual scores per player
        games.forEach((game: { leaderboard: any[]; }) => {
            if (game.leaderboard) {
                game.leaderboard.forEach(entry => {
                    const playerName = entry.userName;
                    const playerPoints = entry.points || 0;

                    if (!playerMap.has(playerName)) {
                        playerMap.set(playerName, {
                            name: playerName,
                            totalPoints: 0,
                            scores: [],
                            gamesPlayed: 0
                        });
                    }

                    const playerData = playerMap.get(playerName);
                    playerData.scores.push(playerPoints);
                    playerData.gamesPlayed += 1;
                });
            }
        });

        // Add penalty scores for missing games and calculate total points with dropNLowest
        playerMap.forEach(playerData => {
            const missingGames = games.length - playerData.gamesPlayed;
            // Add penalty scores for each missing game
            for (let i = 0; i < missingGames; i++) {
                playerData.scores.push(tournamentAppConfiguration.missingGamePenalty);
            }
            
            // Apply dropNLowest logic
            if (tournamentAppConfiguration.dropNLowest > 0 && playerData.scores.length > tournamentAppConfiguration.dropNLowest) {
                // Sort scores to identify lowest ones
                const sortedScores = [...playerData.scores].sort((a, b) => 
                    tournamentAppConfiguration.morePointsIsBetter ? a - b : b - a
                );
                // Drop N lowest scores
                const scoresToKeep = sortedScores.slice(tournamentAppConfiguration.dropNLowest);
                playerData.totalPoints = scoresToKeep.reduce((sum: number, score: number) => sum + score, 0);
            } else {
                // No dropping, sum all scores
                playerData.totalPoints = playerData.scores.reduce((sum: number, score: number) => sum + score, 0);
            }
        })

        // Convert map to array and sort by total points
        const playersArray = Array.from(playerMap.values());

        if (tournamentAppConfiguration.morePointsIsBetter) {
            playersArray.sort((a, b) => b.totalPoints - a.totalPoints);
        } else {
            playersArray.sort((a, b) => a.totalPoints - b.totalPoints);
        }

        // Find the current user's position in the array
        const userIndex = playersArray.findIndex(player =>
            player.name.toLowerCase() === username.toLowerCase()
        );

        // Calculate points needed to increase rank
        if (userIndex > 0) {
            // There's a player above the current user
            pointsToNextRank = playersArray[userIndex - 1].totalPoints - playersArray[userIndex].totalPoints;
            // If pointsToNextRank is 0, set it to 1
            if (pointsToNextRank === 0) {
                pointsToNextRank = 1;
            }

            // Calculate points needed to reach rank 50 if rank is worse than 50
            const userRank = parseInt(overallRank.replace('#', ''));
            if (userRank > tournamentAppConfiguration.cutoffRank) {
                // Check if there are at least CUTOFF amount players in the tournament
                if (playersArray.length >= tournamentAppConfiguration.cutoffRank) {
                    // Find the player at rank CUTOFF (index CUTOFF - 1)
                    const rankCutoffPlayer = playersArray[tournamentAppConfiguration.cutoffRank - 1];
                    if (rankCutoffPlayer) {
                        // Calculate points needed to reach rank CUTOFF
                        pointsToCutoff = rankCutoffPlayer.totalPoints - playersArray[userIndex].totalPoints; // Crash part here?
                        if (tournamentAppConfiguration.morePointsIsBetter && pointsToCutoff <= 0) {
                            pointsToCutoff = null;
                        } else if (!tournamentAppConfiguration.morePointsIsBetter && pointsToCutoff >= 0) {
                            pointsToCutoff = null;
                        }
                    }
                }
            }
        }

    }
    tournamentSummaryData.pointsToCutoff = pointsToCutoff
    tournamentSummaryData.pointsToNextRank = pointsToNextRank
    
    // Calculate overall rank
    if (games && games.length > 0 && username) {
        const playerMap = new Map();
        
        games.forEach((game: { leaderboard: any[]; }) => {
            if (game.leaderboard) {
                game.leaderboard.forEach(entry => {
                    const playerName = entry.userName;
                    const playerPoints = entry.points || 0;

                    if (!playerMap.has(playerName)) {
                        playerMap.set(playerName, { name: playerName, scores: [], gamesPlayed: 0 });
                    }

                    const playerData = playerMap.get(playerName);
                    playerData.scores.push(playerPoints);
                    playerData.gamesPlayed += 1;
                });
            }
        });

        playerMap.forEach(playerData => {
            const missingGames = games.length - playerData.gamesPlayed;
            for (let i = 0; i < missingGames; i++) {
                playerData.scores.push(tournamentAppConfiguration.missingGamePenalty);
            }
            
            if (tournamentAppConfiguration.dropNLowest > 0 && playerData.scores.length > tournamentAppConfiguration.dropNLowest) {
                const sortedScores = [...playerData.scores].sort((a, b) => 
                    tournamentAppConfiguration.morePointsIsBetter ? a - b : b - a
                );
                playerData.totalPoints = sortedScores.slice(tournamentAppConfiguration.dropNLowest).reduce((sum: number, score: number) => sum + score, 0);
            } else {
                playerData.totalPoints = playerData.scores.reduce((sum: number, score: number) => sum + score, 0);
            }
        });

        const playersArray = Array.from(playerMap.values()).sort((a, b) => 
            tournamentAppConfiguration.morePointsIsBetter ? b.totalPoints - a.totalPoints : a.totalPoints - b.totalPoints
        );

        const userIndex = playersArray.findIndex(player => 
            player.name.toLowerCase() === username.toLowerCase()
        );

        tournamentSummaryData.overallRank = userIndex !== -1 ? userIndex + 1 : null;
    } else {
        tournamentSummaryData.overallRank = null;
    }
    
    return tournamentSummaryData
}

export function calculateFullTournamentRankings(
    games: any,
    tournamentAppConfiguration: any
) {
    const playerMap = new Map();
    
    if (!games || games.length === 0) return [];

    // Process each game to collect individual scores per player
    games.forEach((game: { leaderboard: any[]; }) => {
        if (game.leaderboard) {
            game.leaderboard.forEach(entry => {
                const playerName = entry.userName;
                const playerPoints = entry.points || 0;

                if (!playerMap.has(playerName)) {
                    playerMap.set(playerName, { 
                        name: playerName, 
                        scores: [], 
                        gamesPlayed: 0,
                        initials: entry.initials || ""
                    });
                }

                const playerData = playerMap.get(playerName);
                playerData.scores.push(playerPoints);
                playerData.gamesPlayed += 1;
                if (entry.initials && !playerData.initials) {
                    playerData.initials = entry.initials;
                }
            });
        }
    });

    // Add penalty scores for missing games and calculate total points
    playerMap.forEach(playerData => {
        const missingGames = games.length - playerData.gamesPlayed;
        for (let i = 0; i < missingGames; i++) {
            playerData.scores.push(tournamentAppConfiguration.missingGamePenalty);
        }
        
        // Apply dropNLowest logic
        if (tournamentAppConfiguration.dropNLowest > 0 && playerData.scores.length > tournamentAppConfiguration.dropNLowest) {
            const sortedScores = [...playerData.scores].sort((a, b) => 
                tournamentAppConfiguration.morePointsIsBetter ? a - b : b - a
            );
            playerData.totalPoints = sortedScores.slice(tournamentAppConfiguration.dropNLowest).reduce((sum: number, score: number) => sum + score, 0);
        } else {
            playerData.totalPoints = playerData.scores.reduce((sum: number, score: number) => sum + score, 0);
        }
    });

    // Convert map to array, sort by total points, and assign ranks
    return Array.from(playerMap.values())
        .sort((a, b) => 
            tournamentAppConfiguration.morePointsIsBetter ? b.totalPoints - a.totalPoints : a.totalPoints - b.totalPoints
        )
        .map((player, index) => ({
            userName: player.name,
            totalPoints: player.totalPoints,
            rank: index + 1,
            initials: player.initials
        }));
}

class TournamentSummaryData {
    totalPoints: number
    initialsUsed: Set<string>
    bestRank: number
    worstRank: number
    bestGame: string
    // Track all games with the worst rank
    worstGames: string[]
    unrankedGames: string[] // Track unranked games
    // percentage: number
    pointsToNextRank: number | null
    pointsToCutoff: number | null
    overallRank: number | null

    constructor() {
        this.totalPoints = 0
        this.initialsUsed = new Set<string>()
        this.bestRank = Infinity
        this.worstRank = 0
        this.bestGame = ""
        this.worstGames = []
        this.unrankedGames = []
        this.pointsToNextRank = 0
        this.pointsToCutoff = 0
        this.overallRank = null
        // this.percentage = 0
    }
}




